import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { verificationAPI } from '../../services/api';

// Async thunks
export const fetchScrapData = createAsyncThunk(
  'verification/fetchScrapData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await verificationAPI.getScrapData();
      if (response.status === 'success') {
        return response.data;
      }
      return rejectWithValue('Failed to fetch scrap data');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch scrap data');
    }
  }
);

export const verifyContent = createAsyncThunk(
  'verification/verifyContent',
  async ({ contentId, affiliateId, status, notes }, { rejectWithValue, dispatch }) => {
    try {
      const response = await verificationAPI.verifyContent({
        contentId,
        affiliateId,
        status,
        notes,
      });

      if (response.status === 'success') {
        // Refresh scrap data after verification
        dispatch(fetchScrapData());
        return response;
      }
      return rejectWithValue('Verification failed');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Verification failed');
    }
  }
);

const initialState = {
  scrapData: [],
  currentItem: null,
  loading: false,
  verifying: false,
  error: null,
  lastFetch: null,
  filters: {
    platform: 'all', // all, youtube, google
    status: 'pending', // all, pending, verified, rejected
    searchQuery: '',
  },
  stats: {
    total: 0,
    pending: 0,
    verified: 0,
    rejected: 0,
  },
};

const verificationSlice = createSlice({
  name: 'verification',
  initialState,
  reducers: {
    setCurrentItem: (state, action) => {
      state.currentItem = action.payload;
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setFilterPlatform: (state, action) => {
      state.filters.platform = action.payload;
    },
    setFilterStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setFilterSearchQuery: (state, action) => {
      state.filters.searchQuery = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateStats: (state) => {
      const stats = {
        total: state.scrapData.length,
        pending: 0,
        verified: 0,
        rejected: 0,
      };

      state.scrapData.forEach((item) => {
        if (item.verification_status === 'pending') {
          stats.pending++;
        } else if (item.verification_status === 'approved') {
          stats.verified++;
        } else if (item.verification_status === 'rejected') {
          stats.rejected++;
        }
      });

      state.stats = stats;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Scrap Data
      .addCase(fetchScrapData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScrapData.fulfilled, (state, action) => {
        state.loading = false;
        state.scrapData = action.payload;
        state.lastFetch = new Date().toISOString();

        // Update stats
        const stats = {
          total: action.payload.length,
          pending: 0,
          verified: 0,
          rejected: 0,
        };

        action.payload.forEach((item) => {
          if (!item.verification_status || item.verification_status === 'pending') {
            stats.pending++;
          } else if (item.verification_status === 'approved') {
            stats.verified++;
          } else if (item.verification_status === 'rejected') {
            stats.rejected++;
          }
        });

        state.stats = stats;
      })
      .addCase(fetchScrapData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify Content
      .addCase(verifyContent.pending, (state) => {
        state.verifying = true;
        state.error = null;
      })
      .addCase(verifyContent.fulfilled, (state) => {
        state.verifying = false;
        state.currentItem = null;
      })
      .addCase(verifyContent.rejected, (state, action) => {
        state.verifying = false;
        state.error = action.payload;
      });
  },
});

export const {
  setCurrentItem,
  clearCurrentItem,
  setFilters,
  setFilterPlatform,
  setFilterStatus,
  setFilterSearchQuery,
  clearError,
  updateStats,
} = verificationSlice.actions;

export default verificationSlice.reducer;
